/**
 * hcCrypto.js — HustleChat client-side end-to-end encryption.
 *
 * ECDH-P256 + HKDF-SHA-256 → AES-256-GCM, all via WebCrypto so we add
 * no new dependencies.
 *
 * Private key never leaves localStorage. The server only ever sees
 * ciphertext + IV + the sender's ephemeral public key.
 *
 * The "safety" layer (keyword blocklist) runs BEFORE encryption and
 * never sends cleartext to the server unless the user opts to file a
 * report on a received message.
 */

const STORE_PRIV = "hc_ecdh_priv_jwk";
const STORE_PUB  = "hc_ecdh_pub_jwk";

// ── Key management ────────────────────────────────────────────────
export async function ensureKeyPair() {
  let priv = localStorage.getItem(STORE_PRIV);
  let pub  = localStorage.getItem(STORE_PUB);
  if (priv && pub) {
    return { privJwk: JSON.parse(priv), pubJwk: JSON.parse(pub) };
  }
  const kp = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveKey", "deriveBits"],
  );
  const privJwk = await crypto.subtle.exportKey("jwk", kp.privateKey);
  const pubJwk  = await crypto.subtle.exportKey("jwk", kp.publicKey);
  localStorage.setItem(STORE_PRIV, JSON.stringify(privJwk));
  localStorage.setItem(STORE_PUB,  JSON.stringify(pubJwk));
  return { privJwk, pubJwk };
}

export async function getMyPubJwk() {
  const { pubJwk } = await ensureKeyPair();
  return pubJwk;
}

async function importPriv(jwk) {
  return crypto.subtle.importKey(
    "jwk", jwk, { name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"],
  );
}

async function importPub(jwk) {
  return crypto.subtle.importKey(
    "jwk", jwk, { name: "ECDH", namedCurve: "P-256" }, false, [],
  );
}

// ── Key agreement → AES-GCM session key ───────────────────────────
async function deriveAesKey(myPriv, theirPub) {
  const sharedBits = await crypto.subtle.deriveBits(
    { name: "ECDH", public: theirPub }, myPriv, 256);
  return crypto.subtle.importKey(
    "raw", sharedBits, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

// ── Encrypt to recipient's JWK pubkey ─────────────────────────────
export async function encryptFor(recipientPubJwk, cleartext) {
  const { privJwk } = await ensureKeyPair();
  const myPriv = await importPriv(privJwk);
  const theirPub = await importPub(recipientPubJwk);
  const aesKey = await deriveAesKey(myPriv, theirPub);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const buf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    aesKey,
    new TextEncoder().encode(cleartext),
  );
  return { ciphertext: bufToB64(buf), iv: bufToB64(iv) };
}

// ── Decrypt incoming ciphertext ───────────────────────────────────
export async function decryptFrom(senderPubJwk, ciphertextB64, ivB64) {
  const { privJwk } = await ensureKeyPair();
  const myPriv  = await importPriv(privJwk);
  const theirPub = await importPub(senderPubJwk);
  const aesKey  = await deriveAesKey(myPriv, theirPub);
  try {
    const buf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: b64ToBuf(ivB64) },
      aesKey,
      b64ToBuf(ciphertextB64),
    );
    return new TextDecoder().decode(buf);
  } catch {
    return "[decryption failed — message corrupted or wrong key]";
  }
}

// ── Base64 helpers (Uint8Array ⇄ string) ─────────────────────────
function bufToB64(buf) {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}

function b64ToBuf(b64) {
  const s = atob(b64);
  const u = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i);
  return u.buffer;
}

// ══════════════════════════════════════════════════════════════════
// Safety — client-side pre-send blocklist (kept in sync with server's
// SAFETY_KEYWORDS in hp_hustlechat.py).
// ══════════════════════════════════════════════════════════════════
export const SAFETY_RULES = [
  { cat: "csam",    rx: /(child porn|underage|loli\b|kid pics nsfw|minor pics)/i },
  { cat: "drugs",   rx: /(cocaine|meth(?!od)|heroin|fentanyl|mdma|lsd\b|ecstasy|ice for sale|deal weed|molly for sale|buy pills|ketamine)/i },
  { cat: "weapons", rx: /(unregistered gun|buy a gun|ghost gun|silencer for sale|ar-15 for sale)/i },
  { cat: "explicit",rx: /(send nudes|onlyfans link|\bporn\b|\bxxx\b|\bnsfw\b)/i },
  { cat: "scam",    rx: /(bank login|verify your ssn|send btc to|wire transfer urgently)/i },
];

export function safetyScreen(text) {
  if (!text) return null;
  for (const { cat, rx } of SAFETY_RULES) {
    if (rx.test(text)) return cat;
  }
  return null;
}
